import { Close as CloseIcon } from '@mui/icons-material'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Translate } from 'common'
import { defaultTheme } from '../../../theme'
import { getFilterGroupTitleText } from '../getFilterGroupTitle'
import { ListItem } from '../interfaces'
import { partitionFilterOptions } from '../partitionFilterOptions'
import { CheckboxListWrapper } from './CheckboxListWrapper'

interface FilterGroupSearchDropdownProps {
  inputId: string
  groupName: string
  options: ListItem[]
  onChange: (filterName: string, selectedOptions: ListItem[]) => void
}

export const FilterGroupSearchDropdown = ({
  inputId,
  groupName,
  options,
  onChange,
}: FilterGroupSearchDropdownProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const sectionTitle = getFilterGroupTitleText(groupName)
  const searchPlaceholder = Translate('filterGroupSearch', [sectionTitle])

  const { visible: searchResults, hasQuery } = useMemo(
    () => partitionFilterOptions(options, searchQuery),
    [options, searchQuery]
  )

  const showDropdown = isFocused && hasQuery
  const showNoResults = showDropdown && searchResults.length === 0

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDropdownChange = (
    _: string,
    activeFromDropdown: ListItem[]
  ): void => {
    const dropdownValues = new Set(searchResults.map((option) => option.value))
    const activeValues = new Set(
      activeFromDropdown.map((option) => option.value)
    )
    const mergedOptions = options.map((option) => {
      if (!dropdownValues.has(option.value)) {
        return option
      }
      return { ...option, active: activeValues.has(option.value) }
    })
    onChange(
      groupName,
      mergedOptions.filter((option) => option.active)
    )
  }

  const handleClear = (): void => {
    setSearchQuery('')
  }

  return (
    <SearchWrapper ref={containerRef}>
      <SearchContainer $isFocused={isFocused}>
        <SearchInput
          id={inputId}
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          aria-expanded={showDropdown}
          aria-controls={`${inputId}-results`}
          autoComplete="off"
        />
        {searchQuery.length > 0 && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label={Translate('clear')}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </ClearButton>
        )}
      </SearchContainer>
      {showDropdown && (
        <ResultsDropdown id={`${inputId}-results`} role="listbox">
          {showNoResults ? (
            <NoResultsMessage>
              {Translate('filterGroupSearchNoResults', [searchQuery.trim()])}
            </NoResultsMessage>
          ) : (
            <CheckboxListWrapper
              groupName={groupName}
              options={searchResults}
              onChange={handleDropdownChange}
            />
          )}
        </ResultsDropdown>
      )}
    </SearchWrapper>
  )
}

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
  width: 100%;
`

const SearchContainer = styled.div<{ $isFocused: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid
    ${(p) =>
      p.$isFocused
        ? defaultTheme.colors.blueLight
        : defaultTheme.colors.border};
  border-radius: 4px;
  padding: 8px 12px;
  background-color: ${defaultTheme.colors.white};
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 0;
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 20px;
  color: ${defaultTheme.colors.text};

  &::placeholder {
    color: ${defaultTheme.colors.greyDark};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${defaultTheme.colors.greyDark};
  flex-shrink: 0;
`

const ResultsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px 12px;
  background: ${defaultTheme.colors.white};
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`

const NoResultsMessage = styled.div`
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 20px;
  color: ${defaultTheme.colors.greyDark};
  padding: 4px 0;
`
